import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Paper,
  Fade,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

interface ResponseRendererProps {
  response: string;
  copied: boolean;
  onCopy: () => void;
  isLoading?: boolean;
  appointmentId?: string;
}

export const ResponseRenderer: React.FC<ResponseRendererProps> = ({
  response,
  copied,
  onCopy,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          gap: 2,
        }}
      >
        <CircularProgress size={32} sx={{ color: 'primary.main' }} />
        <Typography variant="body2" color="text.secondary">
          Gerando resposta da IA...
        </Typography>
      </Box>
    );
  }

  if (!response) return null;

  return (
    <Fade in={!!response} timeout={500}>
      <Paper
        elevation={3}
        sx={{
          p: 2.5,
          bgcolor: '#f5fafd',
          borderLeft: '3px solid #00ae9a',
          borderRadius: 4,
          position: 'relative',
          maxHeight: { xs: '50vh', sm: '40vh' },
          overflow: 'auto',
          transition: 'box-shadow 0.2s',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} color="primary.main">
            Resposta gerada
          </Typography>
          <Tooltip title={copied ? 'Copiado!' : 'Copiar resposta'}>
            <IconButton
              size="small"
              onClick={onCopy}
              sx={{ color: 'primary.main' }}
              aria-label="Copiar resposta"
            >
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ fontSize: 15 }}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              code: ({ className, children }) => {
                const isInlineCode = !className;
                return isInlineCode ? (
                  <code
                    style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      padding: '2px 4px',
                      borderRadius: 4,
                      fontSize: '0.95em',
                    }}
                  >
                    {children}
                  </code>
                ) : (
                  <pre
                    style={{
                      backgroundColor: '#2b2b2b',
                      color: '#fff',
                      padding: '12px',
                      borderRadius: 8,
                      overflow: 'auto',
                      fontSize: '0.95em',
                    }}
                  >
                    <code className={className}>{children}</code>
                  </pre>
                );
              },
              a: ({ node, ...props }) => (
                <a
                  {...props}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#0277bd',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                />
              ),
            }}
          >
            {response}
          </ReactMarkdown>
        </Box>
      </Paper>
    </Fade>
  );
};
